/**
 * Subject and description, whichever path the employee took to get here.
 *
 * The description is required unless the caller says otherwise: an offering
 * form asks its own questions, so there it is an optional catch-all.
 */

import { Field, Input, Textarea } from "@norican/react";

import { HighlightedField } from "@/components/common";
import { useT } from "@/i18n";
import { useFieldHighlight } from "@/lib/field-highlight";

import {
  DESCRIPTION_MAX_LENGTH,
  DETAILS_MIN_LENGTH,
  SUBJECT_MAX_LENGTH,
  SUBJECT_MIN_LENGTH,
  type ValidationKey,
} from "../lib/validation";

export interface TicketBasicsFieldsProps {
  subject: string;
  description: string;
  onSubjectChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  subjectError: ValidationKey | null;
  descriptionError: ValidationKey | null;
  subjectLabel?: string;
  descriptionLabel?: string;
  /** Hint under the description. `null` shows none. */
  descriptionHint?: string | null;
  /** Mark the description required. Defaults to true. */
  descriptionRequired?: boolean;
  /** What the app filled in, for the field-state tint (5 Oct); null: nothing. */
  prefilledSubject?: string | null;
  prefilledDescription?: string | null;
}

/** Translates a validation key, filling in the limit it refers to. */
export function useValidationMessage() {
  const { t } = useT("tickets");
  return (key: ValidationKey | null): string | undefined => {
    switch (key) {
      case null:
        return undefined;
      case "subjectRequired":
        return t("validation.subjectRequired");
      case "subjectTooShort":
        return t("validation.subjectTooShort", { min: SUBJECT_MIN_LENGTH });
      case "subjectTooLong":
        return t("validation.subjectTooLong", { max: SUBJECT_MAX_LENGTH });
      case "descriptionRequired":
        return t("validation.descriptionRequired");
      case "descriptionTooLong":
        return t("validation.descriptionTooLong", { max: DESCRIPTION_MAX_LENGTH });
      case "detailsRequired":
        return t("validation.detailsRequired");
      case "detailsTooShort":
        return t("validation.detailsTooShort", { min: DETAILS_MIN_LENGTH });
    }
  };
}

export function TicketBasicsFields({
  subject,
  description,
  onSubjectChange,
  onDescriptionChange,
  subjectError,
  descriptionError,
  subjectLabel,
  descriptionLabel,
  descriptionHint,
  descriptionRequired = true,
  prefilledSubject = null,
  prefilledDescription = null,
}: TicketBasicsFieldsProps) {
  const { t } = useT("tickets");
  const message = useValidationMessage();
  const subjectState = useFieldHighlight(prefilledSubject, subject, true);
  const descriptionState = useFieldHighlight(
    prefilledDescription,
    description,
    descriptionRequired,
  );

  return (
    <>
      <HighlightedField state={subjectState} fieldKey="subject">
        <Field
          name="subject"
          label={subjectLabel ?? t("field.subject")}
          hint={t("create.subjectHint")}
          error={message(subjectError)}
          required
        >
          <Input
            name="subject"
            value={subject}
            onChange={(event) => onSubjectChange(event.target.value)}
            placeholder={t("create.subjectPlaceholder")}
            maxLength={SUBJECT_MAX_LENGTH}
          />
        </Field>
      </HighlightedField>

      <HighlightedField state={descriptionState} fieldKey="description">
        <Field
          name="description"
          label={descriptionLabel ?? t("field.description")}
          hint={
            descriptionHint === null ? undefined : (descriptionHint ?? t("create.descriptionHint"))
          }
          error={message(descriptionError)}
          required={descriptionRequired}
        >
          <Textarea
            name="description"
            value={description}
            onChange={(event) => onDescriptionChange(event.target.value)}
            placeholder={t("create.descriptionPlaceholder")}
            rows={5}
            autoResize
            maxLength={DESCRIPTION_MAX_LENGTH}
          />
        </Field>
      </HighlightedField>
    </>
  );
}
